import {
  AddIcon,
  ArrowLeftIcon,
  CMSDefaultIcon,
  ElementComponentIcon,
  IconButton,
  ListBox,
  ListBoxItem,
  Popover,
  PopoverContent,
  PopoverTrigger,
  RowItem,
  RowItemBody,
  SearchableList,
  SearchableListBox,
  SearchableListInput,
  SearchableListItem,
  SearchableListItemBody,
  SearchDefaultIcon,
  Text,
  View,
  VStack,
  type ButtonPrefixSuffix,
  type FilterOptionsType,
  type OptionType,
} from '@packages/systems/spring';
import React, {memo, useState, useMemo, useCallback, useEffect} from 'react';
import {selectCMSCollections} from '@packages/systems/plugins/CMS';
import {useDesignerSelector} from '@packages/systems/designer/flux';
import {selectComponents} from '@packages/utilities/platform-selectors';
import {getName} from '@packages/systems/wfdl-Qualified';
import {Component} from '@packages/systems/wfdl';
import {isUserComponent, isCodeComponent} from '@packages/utilities/platform';
import {pluralize} from '@packages/utilities/string-utils';
import {emotionStyled, tokens} from '@packages/utilities/styled';

// cms is undefined so Collections render with the default (white) text color.
const CONTENT_ITEM_COLORS = {
  cms: undefined,
  component: 'green',
} as const;

// Right-align the item count: Spring lays the name + count in one flex row
// (the div after the prefix icon); spacing it apart pushes the count to the end.
const ContentRowBody = emotionStyled(SearchableListItemBody)({
  '[data-row-prefix] + div': {
    justifyContent: 'space-between',
  },
  // RowItem's compact density pads the right (4px) less than the left (8px);
  // match the left so the item count sits symmetrically. `&&` outranks the
  // single-class density variant.
  '&&': {
    paddingRight: tokens.ui.rowItem.padding.compact.left,
  },
});

export type ContentCategory = 'cms' | 'component';

export interface CMSCollectionItem {
  label: string;
  value: string;
  fields?: Array<{
    slug: string;
    type: string;
    name: string;
    required?: boolean;
  }>;
  icon?: ButtonPrefixSuffix;
}

export interface ComponentItem {
  label: string;
  value: string;
  icon?: ButtonPrefixSuffix;
}

export interface SelectedItems {
  cmsCollections: CMSCollectionItem[];
  components: ComponentItem[];
}

/**
 * An action row shown in the category menu that, instead of drilling into a
 * sub-list, invokes a callback and closes the popover (e.g. AICC's "Image"
 * option which opens the file picker).
 */
export interface ActionItem {
  id: string;
  label: string;
  icon?: ButtonPrefixSuffix;
  onSelect: () => void;
}

const CATEGORY_ITEMS: OptionType[] = [
  {
    label: 'Collections',
    value: 'category-cms',
    prefix: CMSDefaultIcon,
    description: '',
  },
  {
    label: 'Components',
    value: 'category-component',
    prefix: <ElementComponentIcon />,
    description: '',
  },
];

const DEFAULT_CATEGORIES: ContentCategory[] = ['cms', 'component'];
const EMPTY_ACTIONS: ActionItem[] = [];

// Default popover width, matching the App Gen (Orion) picker so the popover
// is the same width across every surface.
const DEFAULT_POPOVER_WIDTH = 260;

export interface AddContentPopoverProps {
  selectedValues: SelectedItems;
  onChange: (items: SelectedItems) => void;
  /**
   * Selection mode for the drilled-in item list. `'multiple'` shows a checkbox
   * per row and keeps the popover open for picking several; `'single'` shows no
   * checkbox and closes the popover on pick. Defaults to `'multiple'`.
   */
  selectionMode?: 'single' | 'multiple';
  categories?: ContentCategory[];
  /** Action rows shown in the category menu (e.g. "Image" → open file picker). */
  actions?: ActionItem[];
  /**
   * Returns a collection's item count (keyed by collection id) for display as
   * secondary text. The Designer collection store does not carry live counts,
   * so the host injects them (e.g. from the localized item-count selector).
   * When omitted, no counts are shown.
   */
  getItemCount?: (collectionId: string) => number | undefined;
  placement?: 'bottom-start' | 'top-start';
  isDisabled?: boolean;
  width?: number | string;
  analytics?: {
    onOpened?(): void;
    onClosed?(): void;
    onOptionSelected?(counts: {
      cmsCollections: number;
      components: number;
    }): void;
  };
  onContextAdded?: () => void;
  autoOpen?: boolean;
  onAutoOpenConsumed?: () => void;
  contentWrapper?: (node: React.ReactNode) => React.ReactNode;
}

export const AddContentPopover = memo(
  ({
    selectedValues,
    onChange,
    selectionMode = 'multiple',
    categories = DEFAULT_CATEGORIES,
    actions = EMPTY_ACTIONS,
    getItemCount,
    placement = 'top-start',
    isDisabled = false,
    width = DEFAULT_POPOVER_WIDTH,
    analytics,
    onContextAdded,
    autoOpen = false,
    onAutoOpenConsumed,
    contentWrapper,
  }: AddContentPopoverProps) => {
    const cmsCollections = useDesignerSelector(selectCMSCollections);
    const components = useDesignerSelector(selectComponents);

    // When only one category is available, skip the category-chooser view and
    // start directly in that category.
    const singleCategory =
      categories.length === 1 && actions.length === 0 ? categories[0] : null;
    const initialView = singleCategory ?? 'category';

    const [isOpen, setIsOpen] = useState(false);
    const [currentView, setCurrentView] = useState<
      'category' | 'cms' | 'component'
    >(initialView);
    const [inputValue, setInputValue] = useState('');

    useEffect(() => {
      if (autoOpen) {
        setIsOpen(true);
        onAutoOpenConsumed?.();
      }
    }, [autoOpen, onAutoOpenConsumed]);

    // Transform CMS collections to SearchableList option format
    const cmsOptions = useMemo(() => {
      if (!cmsCollections) {
        return [];
      }

      const options: OptionType[] = [];

      cmsCollections.forEach((collection) => {
        if (!collection) {
          return;
        }

        const collectionName = collection.name;
        const collectionId = collection._id;

        if (!collectionName || !collectionId) {
          return;
        }

        const itemCount = getItemCount?.(collectionId);
        options.push({
          label: collectionName,
          value: `cms-${collectionId}`,
          prefix: CMSDefaultIcon,
          description:
            typeof itemCount === 'number'
              ? `${itemCount} ${pluralize(itemCount, 'item')}`
              : '',
        });
      });

      return options;
    }, [cmsCollections, getItemCount]);

    // Transform user components to SearchableList option format
    const componentOptions = useMemo(() => {
      if (!components) {
        return [];
      }

      const options: OptionType[] = [];

      components.forEach((component, componentName) => {
        if (
          !component ||
          !componentName ||
          !isUserComponent(componentName) ||
          isCodeComponent(components)(componentName)
        ) {
          return;
        }

        const displayName = Component.getDisplayName(component);
        const qualifiedName = getName(componentName);

        if (!qualifiedName) {
          return;
        }

        options.push({
          label: displayName || qualifiedName,
          value: `component-${qualifiedName}`,
          prefix: <ElementComponentIcon />,
          description: '',
        });
      });

      return options;
    }, [components]);

    // All content items for search
    const allContentItems = useMemo(
      () => [...cmsOptions, ...componentOptions],
      [cmsOptions, componentOptions]
    );

    // Category options limited to the categories this picker supports, plus any
    // action rows (e.g. "Image") that fire a callback instead of drilling in.
    const categoryItems = useMemo(() => {
      const drillIn = CATEGORY_ITEMS.filter((item) =>
        categories.includes(item.value === 'category-cms' ? 'cms' : 'component')
      );
      const actionOptions: OptionType[] = actions.map((action) => ({
        label: action.label,
        value: `action-${action.id}`,
        prefix: action.icon,
        description: '',
      }));
      return [...drillIn, ...actionOptions];
    }, [categories, actions]);

    // Custom filter function to handle view-specific filtering
    const customFilterOptions = useCallback<NonNullable<FilterOptionsType>>(
      ({targetValue, options}) => {
        // When searching from specific view, search only that category's items
        if (targetValue.trim()) {
          const currentCategoryItems =
            currentView === 'cms' ? cmsOptions : componentOptions;
          return currentCategoryItems.filter(
            (item) =>
              item.label.toLowerCase().includes(targetValue.toLowerCase()) ||
              (item.description &&
                String(item.description)
                  .toLowerCase()
                  .includes(targetValue.toLowerCase()))
          );
        }

        // When not searching, return the current view options
        return options;
      },
      [currentView, cmsOptions, componentOptions]
    );

    // Determine what options to show based on current view
    const displayOptions = useMemo(() => {
      switch (currentView) {
        case 'category':
          return categoryItems;
        case 'cms':
          return cmsOptions;
        case 'component':
          return componentOptions;
        default:
          return categoryItems;
      }
    }, [currentView, categoryItems, cmsOptions, componentOptions]);

    const handleInputChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        setInputValue(event.target.value);
      },
      []
    );

    const handleClearInput = useCallback(() => {
      setInputValue('');
    }, []);

    const handlePopoverClose = () => {
      setIsOpen(false);
      setInputValue('');
      analytics?.onClosed?.();

      // Reset view after popover closes
      setTimeout(() => {
        setCurrentView(initialView);
      }, 160);
    };

    const handleBackClick = () => {
      setCurrentView('category');
      setInputValue('');
    };

    const handleCategorySelection = (value: string) => {
      if (value === 'category-cms') {
        setCurrentView('cms');
      } else if (value === 'category-component') {
        setCurrentView('component');
      } else if (value.startsWith('action-')) {
        const action = actions.find((a) => `action-${a.id}` === value);
        if (action) {
          action.onSelect();
          handlePopoverClose();
        }
      }
    };

    const handleItemSelection = useCallback(
      (values: string[]) => {
        const itemValues = values.filter(
          (value) => !value.startsWith('category-')
        );

        const selectedCmsCollections: CMSCollectionItem[] = [];
        const selectedComponents: ComponentItem[] = [];

        itemValues.forEach((value) => {
          const foundItem = allContentItems.find(
            (item) => item.value === value
          );

          if (foundItem) {
            if (value.startsWith('cms-')) {
              const collectionId = value.replace('cms-', '');
              const collection = cmsCollections?.find(
                (c) => c._id === collectionId
              );

              let fields: CMSCollectionItem['fields'];
              if (collection && collection.fields) {
                fields = collection.fields.toArray().map((field) => ({
                  slug: field.slug,
                  // TODO: normalize field-type vocabulary to public API names
                  type: field.type,
                  name: field.name,
                  required: field.required,
                }));
              }

              selectedCmsCollections.push({
                label: foundItem.label,
                value: foundItem.value,
                fields,
              });
            } else if (value.startsWith('component-')) {
              selectedComponents.push({
                label: foundItem.label,
                value: foundItem.value,
              });
            }
          }
        });

        // Dispatch event when user selects Collections or Components
        if (
          selectedCmsCollections.length > 0 ||
          selectedComponents.length > 0
        ) {
          onContextAdded?.();
        }

        onChange({
          cmsCollections: selectedCmsCollections,
          components: selectedComponents,
        });
        analytics?.onOptionSelected?.({
          cmsCollections: selectedCmsCollections.length,
          components: selectedComponents.length,
        });
      },
      [allContentItems, onChange, cmsCollections, onContextAdded, analytics]
    );

    // Single-select commits and dismisses on pick (conventional single-select UX).
    const handleSingleItemSelection = (value: string) => {
      handleItemSelection(value ? [value] : []);
      handlePopoverClose();
    };

    const renderOption = useCallback((option: OptionType) => {
      const isCategory = option.value.startsWith('category-');
      const isAction = option.value.startsWith('action-');
      const colorVariant =
        isCategory || isAction
          ? undefined
          : CONTENT_ITEM_COLORS[
              option.value.startsWith('cms-') ? 'cms' : 'component'
            ];

      return (
        <SearchableListItem
          textColorVariant={colorVariant}
          prefix={option.prefix}
          hasChevron={isCategory}
        >
          <ContentRowBody description={option.description || ''}>
            {option.label}
          </ContentRowBody>
        </SearchableListItem>
      );
    }, []);

    const selectedItemValues = [
      ...selectedValues.cmsCollections.map((item) => item.value),
      ...selectedValues.components.map((item) => item.value),
    ];

    const searchableListChildren = (
      <>
        {!singleCategory && (
          <View width="100%">
            <RowItem
              onClick={handleBackClick}
              prefix={<ArrowLeftIcon />}
              density="compact"
            >
              <RowItemBody>
                <Text colorVariant="primary">
                  {currentView === 'cms' ? 'Collections' : 'Components'}
                </Text>
              </RowItemBody>
            </RowItem>
          </View>
        )}

        <View padding="$2">
          <SearchableListInput
            aria-label="Search context to add to AI chat"
            placeholder={`Search for ${currentView === 'cms' ? 'Collections' : 'components'}`}
            prefix={<SearchDefaultIcon />}
            isClearable
            onClear={handleClearInput}
          />
        </View>

        <SearchableListBox maxHeight="70px" />
      </>
    );

    // selectionMode/value/onChange stay per-branch because SearchableList's
    // selection props are a discriminated union that TS can't narrow from a
    // runtime variable.
    const sharedListProps = {
      filterOptions: customFilterOptions,
      inputValue,
      onInputChange: handleInputChange,
      options: displayOptions,
      renderOption,
      shouldFilterOverrideOptionsProp: true,
      renderEmptyStateLabel: () =>
        currentView === 'cms'
          ? 'No matching Collections'
          : 'No matching components',
    };

    const itemList =
      selectionMode === 'multiple' ? (
        <SearchableList
          {...sharedListProps}
          selectionMode="multiple"
          value={selectedItemValues}
          onChange={handleItemSelection}
        >
          {searchableListChildren}
        </SearchableList>
      ) : (
        <SearchableList
          {...sharedListProps}
          selectionMode="single"
          value={selectedItemValues[0] ?? ''}
          onChange={handleSingleItemSelection}
        >
          {searchableListChildren}
        </SearchableList>
      );

    const popoverBody = (
      <VStack spacing={0} paddingY="$2" width={width}>
        {currentView === 'category' ? (
          <ListBox aria-label="Add context to AI chat" selectionMode="none">
            {categoryItems.map((item) => (
              <ListBoxItem
                key={item.value}
                value={item.value}
                prefix={item.prefix}
                hasChevron={item.value.startsWith('category-')}
                onClick={() => handleCategorySelection(item.value)}
              >
                {item.label}
              </ListBoxItem>
            ))}
          </ListBox>
        ) : (
          itemList
        )}
      </VStack>
    );

    return (
      <Popover
        placement={placement}
        shouldShowMask
        maskProps={{colorVariant: 'transparent'}}
        isOpen={isOpen}
        onOpenChange={(open) => {
          if (!open) {
            handlePopoverClose();
          }
        }}
      >
        <PopoverTrigger>
          <IconButton
            Icon={AddIcon}
            label="Add"
            variant="ghost"
            density="comfortable"
            isDisabled={isDisabled}
            isSelected={isOpen}
            onClick={() => {
              setIsOpen(true);
              analytics?.onOpened?.();
            }}
          />
        </PopoverTrigger>
        <PopoverContent aria-label="Add context to AI chat" isSeamless>
          {contentWrapper ? contentWrapper(popoverBody) : popoverBody}
        </PopoverContent>
      </Popover>
    );
  }
);

AddContentPopover.displayName = 'AddContentPopover';
